<template>
  <div>
    <title-panel
      :button-label="t('digitalizationProjectContentTypes.addContentType')"
      :count="contentTypes.length"
      :on-button-click="handleAddContentTypeButtonClick"
      :title="t('digitalizationProjectContentTypes.contentTypes')"
    >
    </title-panel>
    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="contentTypes"
      :loading="isGettingContentTypes"
      :mode="TableMode.CLIENT_SIDE"
      row-clickable
      @row-click="handleContentTypeEditIconClick"
    >
      <template #cell(contentTypeName)="{ data }">
        {{ data?.contentTypeName }}
      </template>
      <template #cell(uploadsCount)="{ data }">
        {{ data?.uploadsCount }}
      </template>
    </table-wrapper>

    <content-type-modal
      ref="modalRef"
      :mode="ProjectType.DIGITALIZATION_PROJECT"
      :project-id="digitalizationProjectId"
      @content-type-added="getContentTypes"
    ></content-type-modal>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios, { AxiosResponse } from 'axios';
  import ContentTypeModal from '@/components/ContentTypeModal/ContentTypeModal.vue';
  import TableWrapper from '@/components/TableWrapper/TableWrapper.vue';
  import TitlePanel from '@/components/TitlePanel/TitlePanel.vue';
  import endpointsList from '@/helpers/endpointsList';
  import {
    IDigitalizationProjectContentTypesAssignmentsSummaryDto,
    ProjectType,
  } from 'types';
  import {
    useTableWrapper,
    TableFiltersBuilder,
    TableFieldsBuilder,
    TableMode,
  } from '@/components/TableWrapper';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';

  const props = defineProps<{
    digitalizationProjectId: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();

  const contentTypes = ref<
    IDigitalizationProjectContentTypesAssignmentsSummaryDto[]
  >([]);
  const isGettingContentTypes = ref(false);

  const { pageOptions, filters } =
    useTableWrapper<IDigitalizationProjectContentTypesAssignmentsSummaryDto>(
      TableMode.CLIENT_SIDE,
      'contentTypeName',
      getContentTypes,
    );

  const filterConfiguration =
    new TableFiltersBuilder<IDigitalizationProjectContentTypesAssignmentsSummaryDto>(
      {
        translationKey: 'digitalizationProjectContentTypes.tableFields',
      },
    )
      .addInputFilter('contentTypeName', { width: '280px', simpleLabel: true })
      .addInputFilter('uploadsCount', { width: '208px', simpleLabel: true })
      .build();

  const tableFields = computed(() =>
    new TableFieldsBuilder<IDigitalizationProjectContentTypesAssignmentsSummaryDto>(
      {
        sortable: true,
        labelTranslationKey: 'digitalizationProjectContentTypes.tableFields',
      },
    )
      .addField('contentTypeName', { width: '50%' })
      .addField('uploadsCount', { width: '50%' })
      .build(),
  );

  async function getContentTypes() {
    try {
      isGettingContentTypes.value = true;

      const response: AxiosResponse<
        IDigitalizationProjectContentTypesAssignmentsSummaryDto[]
      > = await axios.get(
        endpointsList.contentTypeMappings.getDigitalizationProjectContentTypesSummary(
          props.digitalizationProjectId,
        ),
      );

      contentTypes.value = response.data;
    } catch {
      createErrorToast(
        t(
          'digitalizationProjectContentTypes.errors.errorGettingContentTypes.long',
        ),
      );
    } finally {
      isGettingContentTypes.value = false;
    }
  }

  function handleContentTypeEditIconClick(
    row: IDigitalizationProjectContentTypesAssignmentsSummaryDto,
  ) {
    router.push({
      name: Route.CONTENT_TYPE_CONFIGURATION,
      params: {
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION].projectId]:
          props.digitalizationProjectId,
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION].contentTypeId]: row._id,
        [routeParams[Route.CONTENT_TYPE_CONFIGURATION][PROJECT_TYPE_PARAM]]:
          ProjectType.DIGITALIZATION_PROJECT,
      },
    });
  }

  const modalRef = ref<InstanceType<typeof ContentTypeModal> | null>(null);
  function handleAddContentTypeButtonClick() {
    modalRef.value?.show();
  }
</script>
